import { ref, onScopeDispose } from 'vue';
import { useEventListener } from '@vueuse/core';

// A release faster than this (px/ms) is a flick: up opens the sheet to its largest snap
// height, down closes it. Touch keeps the map sheets' (SheetResizer) 0.8; a mouse drags
// faster for the same intent, so it needs a quicker release to count.
export const SHEET_FLICK_SPEED_TOUCH = 0.8;
export const SHEET_FLICK_SPEED_MOUSE = 1.6;
// Speed is measured over the drag's last moments, so a pause before letting go isn't a flick
const FLICK_WINDOW = 100;

// Drag-to-resize for a bottom sheet, from a handle element. It works in sheet heights (px)
// and leaves applying them to the sheet:
// - Dragging calls onDrag(height) at most once per animation frame, with the height capped
//   at maxHeight. There's no floor: the sheet follows the pointer down toward closing.
// - Released below closeHeight, or flicked down, it calls onClose(). Flicked up it settles
//   at the largest snap height; otherwise at the nearest one: onSettle(height).
// - ArrowUp / ArrowDown on the handle step between the snap heights (onSettle).
// onStart() runs before each drag or key step, before any height is reported.
//
// `handle` is an element ref. `options`:
// {
//   getHeight: () => px,           the sheet's current height
//   getSnapHeights: () => [px],    ascending; read when a drag or key step settles
//   maxHeight: () => px,           default: the viewport height
//   closeHeight: px,
//   flickSpeed: { touch, mouse },  px/ms; pen counts as touch
//   onStart, onDrag, onSettle, onClose
// }
export function useSheetDrag(handle, options) {
  const {
    getHeight,
    getSnapHeights,
    maxHeight = () => window.innerHeight,
    closeHeight = 0,
    flickSpeed = {},
    onStart = () => {},
    onDrag = () => {},
    onSettle = () => {},
    onClose = () => {}
  } = options;

  const isDragging = ref(false);

  let startY = 0;
  let startHeight = 0;
  let pointerType = 'mouse';
  // Recent pointer positions, for the release speed
  let samples = [];
  // The latest pointer position, applied on the next animation frame
  let pendingY = null;
  let frameId = null;
  let draggedHeight = 0;

  function getFlickSpeed() {
    return pointerType === 'mouse'
      ? flickSpeed.mouse ?? SHEET_FLICK_SPEED_MOUSE
      : flickSpeed.touch ?? SHEET_FLICK_SPEED_TOUCH;
  }

  // px/ms over the drag's last moments before release; positive is upward (growing)
  function getReleaseSpeed(releaseTime) {
    const last = samples.at(-1);

    if (!last || releaseTime - last.time > FLICK_WINDOW) return 0;

    const first = samples.find(sample => last.time - sample.time <= FLICK_WINDOW);

    if (last === first) return 0;

    return (first.y - last.y) / (last.time - first.time);
  }

  function getNearestHeight(height, snapHeights) {
    return snapHeights.reduce((nearest, snapHeight) =>
      Math.abs(snapHeight - height) < Math.abs(nearest - height) ? snapHeight : nearest);
  }

  function applyPendingPosition() {
    frameId = null;

    if (pendingY === null) return;

    draggedHeight = Math.max(Math.min(startHeight + (startY - pendingY), maxHeight()), 0);
    pendingY = null;

    onDrag(draggedHeight);
  }

  function cancelFrame() {
    if (frameId !== null) cancelAnimationFrame(frameId);
    frameId = null;
  }

  function handlePointerDown(event) {
    onStart();

    isDragging.value = true;
    pointerType = event.pointerType;
    startY = event.clientY;
    startHeight = getHeight();
    draggedHeight = startHeight;
    samples = [{ y: event.clientY, time: event.timeStamp }];

    handle.value.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event) {
    if (!isDragging.value) return;

    // Every move counts toward the release speed; only the latest is drawn
    samples.push({ y: event.clientY, time: event.timeStamp });
    samples = samples.filter(sample => event.timeStamp - sample.time <= FLICK_WINDOW * 2);

    pendingY = event.clientY;
    if (frameId === null) frameId = requestAnimationFrame(applyPendingPosition);
  }

  function handlePointerUp(event) {
    if (!isDragging.value) return;

    // Land on where the pointer was let go, not the last drawn frame
    cancelFrame();
    applyPendingPosition();

    isDragging.value = false;

    // A cancelled drag (the browser took the pointer) only snaps, never flicks
    const speed = event.type === 'pointercancel' ? 0 : getReleaseSpeed(event.timeStamp);
    const snapHeights = getSnapHeights();

    if (draggedHeight < closeHeight || speed < -getFlickSpeed()) {
      onClose();
      return;
    }

    onSettle(speed > getFlickSpeed() ? snapHeights.at(-1) : getNearestHeight(draggedHeight, snapHeights));
  }

  function handleKeydown(event) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

    event.preventDefault();
    onStart();

    // Step to the next snap height up or down, staying at the ends
    const height = getHeight();
    const snapHeights = getSnapHeights();
    const nextHeight = event.key === 'ArrowUp'
      ? snapHeights.find(snapHeight => snapHeight > height + 1) ?? snapHeights.at(-1)
      : snapHeights.findLast(snapHeight => snapHeight < height - 1) ?? snapHeights[0];

    onSettle(nextHeight);
  }

  useEventListener(handle, 'pointerdown', handlePointerDown);
  useEventListener(handle, 'pointermove', handlePointerMove);
  useEventListener(handle, 'pointerup', handlePointerUp);
  useEventListener(handle, 'pointercancel', handlePointerUp);
  useEventListener(handle, 'keydown', handleKeydown);

  onScopeDispose(cancelFrame);

  return { isDragging };
}
